import React, { useState, useRef, useEffect, useCallback } from "react";

import useSearchStore from "../../_Global/SearchStore";

export default function SearchSuggestions () {
    const suggestionsPopupElement = useRef<HTMLOListElement>(null);

    const [suggestionsAreVisible, setSuggestionsAreVisible] = useState(false);

    const setUserSearchTerm = useSearchStore(
        (state) => state.setUserSearchTerm
    );

    const searchSuggestions = useSearchStore(
        (state) => state.searchSuggestions
    );

    const setSearchSuggestions = useSearchStore(
        (state) => state.setSearchSuggestions
    );

    const setFunnelbackQueryParameters = useSearchStore(
        (state) => state.setFunnelbackQueryParameters
    );

    /**
     * Respond to keyboard presses within the context of the popup container
     *
     * @param {KeyboardEvent} event Event that triggered the click
     *
     * @returns void
     */
    const onKeyPress = useCallback((event) => {
        if (event.key === 'ArrowUp') {
            // eslint-disable-next-line no-use-before-define
            switchElementFocus('up');
        }

        if (event.key === 'ArrowDown') {
            // eslint-disable-next-line no-use-before-define
            switchElementFocus('down');
        }
    }, []);

    /**
     * Operations to perform when the component first mounts.
     * These only run once - the first time the component has been added.
     */
    useEffect(() => {
        // Listen for key presses (scoped to the suggestions list)
        if (suggestionsPopupElement.current !== null) {
            suggestionsPopupElement.current.addEventListener(
                'keydown',
                onKeyPress,
                false
            );
        }

        return () => {
            // Remove the listeners when component removed from DOM
            if (suggestionsPopupElement.current !== null) {
                suggestionsPopupElement.current.removeEventListener(
                    'keydown',
                    onKeyPress,
                    false
                );
            }
        };
    }, []);

    /**
     * Respond to focus event changes to the search query input
     */
    useEffect(() => {
        if (searchSuggestions.length !== 0) {
            setSuggestionsAreVisible(true);
        } else {
            setSuggestionsAreVisible(false);
        }
    }, [searchSuggestions]);

    /**
     * Respond to clicks on the query completion suggestion buttons.
     */
    const onSuggestionButtonClick = (evt: React.MouseEvent<HTMLElement>) => {
        const eventTarget = evt.currentTarget as HTMLElement;
        const querySuggestion = eventTarget.getAttribute('data-suggestion');

        evt.preventDefault();

        if (querySuggestion) {
            // Update the search input area with the suggestion
            setUserSearchTerm(querySuggestion);

            // Reset the search suggestions
            setSearchSuggestions([]);

            // Update the Funnelback query with the suggested search term
            setFunnelbackQueryParameters({
                query: querySuggestion
            });
        }
    };

    /**
     * Move the keyboard focus up or down the DOM tree
     */
    function switchElementFocus(direction: string) {
        // Get the current element in focus
        const currentFocusElement:Element | null = document.activeElement;
        let focusElementParent:HTMLElement | null = null;
        let switchOptionParent:Element | null = null;
        let optionFocusElement:HTMLButtonElement | null = null;

        if (
            currentFocusElement !== null &&
            currentFocusElement.className.indexOf(
                    'js-search-suggestion-option'
                ) !== -1 &&
            currentFocusElement.parentElement !== null
        ) {
            focusElementParent = currentFocusElement.parentElement;

            // If it's an option from our popup then switch focus to the next option in the list
            if (direction === 'up') {
                // Find the previous element in the list
                switchOptionParent =
                    focusElementParent.previousElementSibling;
            } else {
                // Find the next element in the list
                switchOptionParent =
                    focusElementParent.nextElementSibling;
            }
        }

        // We don't loop back around, we stop at the last option in the list
        if (
            switchOptionParent !== null
        ) {
            optionFocusElement = switchOptionParent.querySelector('.js-search-suggestion-option');

            if (optionFocusElement !== null) {
                optionFocusElement.focus();
            }
        }
    }

    return (
        <ul
            className={`search-suggestions-listbox elevation-raised-4 custom-dropdown__list ${
                suggestionsAreVisible ? 'custom-dropdown__list--is-active' : ''
            }`}
            role="listbox"
            id="suggestions-listbox"
            aria-label="Search suggestions"
            ref={suggestionsPopupElement}
        >
            {
                searchSuggestions.map(
                    (suggestion: SearchSuggestion) => {
                        return (
                            <li
                                key={suggestion.id}
                                className="body-size-sm custom-dropdown__list-item"
                                role="option"
                                data-suggestion={
                                    suggestion.value
                                }
                                onClick={
                                    onSuggestionButtonClick
                                }
                            >
                                {
                                    suggestion.label
                                }
                            </li>
                        );
                    }
                )
            }
        </ul>
    );
}